'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/language'
import * as DX from './exercises/deepen'

const TokenizerLab = dynamic(() => import('../_components/widgets/LanguageWidgets').then(m => m.TokenizerLab), { ssr: false })

export default function Tokenization() {
  return (
    <Lesson>
      <Step>
        <H>Models only eat numbers</H>
        <P>
          Everything you’ve built works on numbers. Text isn’t numbers. So before a language model can read anything, we chop
          the text into pieces called <strong>tokens</strong>, and give each distinct token an ID — a position in a fixed list
          called the <strong>vocabulary</strong>.
        </P>
        <P>How should we chop? Two obvious options, both flawed:</P>
        <ul className="gd-list">
          <li>
            <strong>Characters</strong>: tiny vocabulary (just letters and symbols), but sentences become very long sequences,
            and the model must learn spelling from scratch.
          </li>
          <li>
            <strong>Whole words</strong>: short sequences, but the vocabulary explodes (every name, typo and new word needs its
            own ID), and unseen words can’t be represented at all.
          </li>
        </ul>
      </Step>

      <Step gate="q-bpe">
        <H>Byte-pair encoding: the best of both</H>
        <P>
          Modern models use something in between, learned from data. <strong>Byte-pair encoding</strong> (BPE) starts with
          single characters, then repeatedly finds the most common <em>pair</em> of adjacent tokens in a big corpus and merges it
          into a new token. After thousands of merges, common words are single tokens, while rare words are built from a few
          reusable pieces.
        </P>
        <Figure n="8.1" caption="Type anything. Switch between tokenizers; for BPE, drag the number of merges and watch common fragments fuse into single tokens. Each colour/number is a token ID.">
          <TokenizerLab />
        </Figure>
        <Quiz
          id="q-bpe"
          question="Why do language models sometimes struggle to count the letters in a word like “strawberry”?"
          options={[
            { label: 'They see tokens like “straw” + “berry”, not individual letters.', correct: true },
            { label: 'They can’t do arithmetic at all.', why: 'They can do a fair amount — the problem here is they never see the letters individually.' },
            { label: 'The word is too long for them.', why: 'Length isn’t the issue — tokenization hides the letters.' },
          ]}
          explain="To the model, a word is a handful of token IDs. Counting characters inside a token means recalling facts about how that token is spelled — something it was never directly shown."
        />
      </Step>

      <Step gate="vocab">
        <H>Build a tokenizer</H>
        <P>
          For our own models we’ll use the simplest scheme — one token per character — so the vocabulary is tiny and you can see
          everything. Build the three pieces every tokenizer has: a vocabulary, <C>encode</C> and <C>decode</C>.
        </P>
        <Exercise
          id="vocab"
          title="A character tokenizer"
          {...EX.vocab}
          hints={[
            <>
              <C>{'[...new Set(text.split(""))].sort()'}</C> gives the sorted distinct characters.
            </>,
            <>
              Fill <C>stoi</C> with <C>{'itos.forEach((ch, i) => (stoi[ch] = i))'}</C>. Encode maps each character through{' '}
              <C>stoi</C>; decode maps each id through <C>itos</C> and joins.
            </>,
          ]}
        />
        <Callout kind="why">
          GPT-style models use vocabularies of roughly 50,000 to 200,000 BPE tokens. A token is, very roughly, ¾ of an English
          word. When an AI company charges “per token”, these are the pieces they’re counting.
        </Callout>
      </Step>

      <Step gate={['bpe', 'q-languages']}>
        <H>Learn the merges yourself</H>
        <P>
          BPE is only a few lines of code: count adjacent pairs, merge the most common one everywhere, and repeat. Run it on
          the stories and watch common fragments like “the” and “ed” become single tokens.
        </P>
        <Exercise
          id="bpe"
          title="countPairs, mergePair, learnMerges"
          {...DX.bpe}
          hints={[
            <>
              <C>{'const key = tokens[i] + "|" + tokens[i + 1]'}</C>, and count it in a Map.
            </>,
            <>
              mergePair: walk the list; when <C>{'tokens[i] === a && tokens[i + 1] === b'}</C>, push <C>a + b</C> and skip one
              extra position.
            </>,
            <>
              learnMerges: <C>k</C> times, find the key with the biggest count, <C>{'best.split("|")'}</C>, record it, and merge.
            </>,
          ]}
        />
        <Quiz
          id="q-languages"
          question="A tokenizer’s merges were learned mostly from English text. What happens to text in other languages?"
          options={[
            { label: 'It gets split into more, smaller tokens — so it costs more and fits less into the context', correct: true },
            { label: 'It can’t be tokenized at all', why: 'Any text can be tokenized, because it can always fall back to single characters or bytes. It just takes more tokens.' },
            { label: 'Nothing changes', why: 'The merges reflect the training text. Words that were rare in it stay in small pieces.' },
          ]}
          explain="The same sentence can take two or three times as many tokens in some languages, which means slower, pricier and shorter-context service for those users. Tokenizer design is a fairness question too."
        />
      </Step>
    </Lesson>
  )
}
